import { useState, useEffect } from 'react';
import { getFinancials } from '../api';
import { colours as c, font, gbp, num } from '../theme';

const Skeleton = ({ width = '100%', height = '20px', style = {} }) => (
  <div className="skeleton" style={{ width, height, ...style }} />
);

/* ---------- charts (pure CSS / SVG, no library) -------------------------- */

const Bars = ({ series }) => {
  const max = Math.max(1, ...series.map(s => s.income));
  return (
    <>
      <div style={S.barsWrap}>
        {series.map((s, i) => (
          <div key={i} style={S.barCol} title={`${s.month}: ${gbp(s.income)}`}>
            <div
              style={{
                width: '100%',
                height: `${Math.max(2, (s.income / max) * 100)}%`,
                background: i === series.length - 1 ? c.teal : '#c9d6c1',
                borderRadius: '2px 2px 0 0',
              }}
            />
          </div>
        ))}
      </div>
      <div style={S.axis}>
        {series.map((s, i) => (
          <span key={i} style={{ flex: 1, textAlign: 'center' }}>
            {s.month.split(' ')[0]}
          </span>
        ))}
      </div>
    </>
  );
};

const Lines = ({ series }) => {
  const max = Math.max(1, ...series.map(s => s.active));
  const W = 480, H = 200, pad = 8;
  const pts = (key) =>
    series
      .map((s, i) => {
        const x = (i / Math.max(1, series.length - 1)) * W;
        const y = H - pad - ((s[key] / max) * (H - pad * 2));
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  return (
    <>
      <div style={{ flex: 1, borderBottom: `1px solid ${c.hairline}`, minHeight: 160 }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
             style={{ width: '100%', height: '100%', display: 'block' }}>
          <polyline points={pts('active')} fill="none" stroke={c.ink}
                    strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
          <polyline points={pts('annual')} fill="none" stroke={c.teal}
                    strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
          <polyline points={pts('monthly')} fill="none" stroke={c.sage}
                    strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <div style={S.axis}>
        {series.map((s, i) => (
          <span key={i} style={{ flex: 1, textAlign: 'center' }}>
            {s.month.split(' ')[0]}
          </span>
        ))}
      </div>
    </>
  );
};

const Donut = ({ gradient, centreTop, centreSub }) => (
  <div style={{ ...S.donut, background: gradient }}>
    <div style={S.donutHole}>
      <span style={S.donutValue}>{centreTop}</span>
      <span style={S.donutLabel}>{centreSub}</span>
    </div>
  </div>
);

const Key = ({ colour, label, meta }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <span style={{ width: 11, height: 11, background: colour, borderRadius: 2, flex: 'none' }} />
    <span style={{ color: c.ink }}>{label}</span>
    {meta && <span style={{ color: c.muted }}>{meta}</span>}
  </div>
);

/* ---------- component ---------------------------------------------------- */

export default function AdminMoney() {
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFinancials()
      .then(res => setF(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !f) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Skeleton height="110px" />
        <Skeleton height="260px" />
      </div>
    );
  }

  const totalActive = f.activeMemberCount || 0;
  const annualPct = totalActive ? (f.activeAnnualCount / totalActive) * 100 : 0;

  const planGradient =
    totalActive === 0
      ? c.hairline
      : `conic-gradient(${c.teal} 0 ${annualPct}%, ${c.sage} ${annualPct}% 100%)`;

  const potDrawn = f.totalPotDrawn || 0;
  const paidPct = potDrawn ? (f.totalPaidOut / potDrawn) * 100 : 0;
  const moneyGradient =
    potDrawn === 0
      ? c.hairline
      : `conic-gradient(${c.teal} 0 ${paidPct}%, ${c.sage} ${paidPct}% 100%)`;

  const stats = [
    ['Collected this year',     gbp(f.totalThisYear)],
    ['Collected all time',      gbp(f.totalAllTime)],
    ['Active members',          `${num(f.activeMemberCount)}`],
    ['Entries held',            num(f.activeEntryCount ?? f.activeMemberCount)],
    ["This month's pot",        gbp(f.currentMonthPot)],
    ['Active annual',           num(f.activeAnnualCount)],
    ['Active monthly',          num(f.activeMonthlyCount)],
    ['Annual subs value',       gbp(f.annualSubsValue)],
    ['Monthly subs, per month', gbp(f.monthlySubsValue)],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      <span style={S.intro}>Everything the treasurer needs for the AGM, in one page</span>

      {/* stat grid */}
      <div style={S.statGrid}>
        {stats.map(([label, value]) => (
          <div key={label} style={S.statCell}>
            <span style={S.statLabel}>{label}</span>
            <span style={S.statValue}>{value}</span>
          </div>
        ))}
      </div>

      <div style={S.chartGrid} className="grid-2">

        {/* money in */}
        <div style={S.card}>
          <div style={S.cardHead}>
            <span style={S.cardTitle}>Money in, last 12 months</span>
          </div>
          <Bars series={f.series || []} />
        </div>

        {/* active members */}
        <div style={S.card}>
          <div style={S.cardHead}>
            <span style={S.cardTitle}>Active members, last 12 months</span>
            <div style={S.legend}>
              <span style={S.legendItem}>
                <span style={{ width: 14, height: 2, background: c.ink }} />Total
              </span>
              <span style={S.legendItem}>
                <span style={{ width: 14, height: 2, background: c.teal }} />Annual
              </span>
              <span style={S.legendItem}>
                <span style={{ width: 14, height: 2, background: c.sage }} />Monthly
              </span>
            </div>
          </div>
          <Lines series={f.series || []} />
        </div>

        {/* plan split */}
        <div style={S.donutCard} className="stack-sm">
          <Donut
            gradient={planGradient}
            centreTop={num(totalActive)}
            centreSub="members"
          />
          <div style={S.donutSide}>
            <span style={S.cardTitle}>Annual vs monthly</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
              <Key
                colour={c.teal}
                label={`Annual · ${num(f.activeAnnualCount)}`}
                meta={`${annualPct.toFixed(0)}%`}
              />
              <Key
                colour={c.sage}
                label={`Monthly · ${num(f.activeMonthlyCount)}`}
                meta={`${(100 - annualPct).toFixed(0)}%`}
              />
            </div>
            <span style={S.note}>
              Both plans put {gbp(f.monthlyRate)} an entry a month into the pot, so
              the split only matters for cash flow. The pot follows entries held
              ({num(f.activeEntryCount ?? f.activeMemberCount)}), not member count.
            </span>
          </div>
        </div>

        {/* where the money went */}
        <div style={S.donutCard} className="stack-sm">
          <Donut
            gradient={moneyGradient}
            centreTop={gbp(potDrawn).replace(/\.00$/, '')}
            centreSub="drawn"
          />
          <div style={S.donutSide}>
            <span style={S.cardTitle}>Where the money went</span>
            {f.drawCount === 0 ? (
              <span style={S.note}>No draws have been run yet.</span>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                  <Key
                    colour={c.teal}
                    label="Prizes paid"
                    meta={`${gbp(f.totalPaidOut)} · ${paidPct.toFixed(0)}%`}
                  />
                  <Key
                    colour={c.sage}
                    label="Kept for the fields"
                    meta={`${gbp(f.totalRetained)} · ${(100 - paidPct).toFixed(0)}%`}
                  />
                </div>
                <span style={S.note}>
                  {gbp(f.totalRetained)} towards mowing, goalposts and the pavilion across{' '}
                  {num(f.drawCount)} draw{f.drawCount === 1 ? '' : 's'}.
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  intro: { fontSize: 14, color: c.muted },

  statGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 1, background: c.hairline, border: `1px solid ${c.hairline}`,
    borderRadius: 3, overflow: 'hidden', fontVariantNumeric: 'tabular-nums',
  },
  statCell: {
    background: c.card, padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0,
  },
  statLabel: { fontSize: 12, color: c.muted },
  statValue: {
    fontFamily: font.serif, fontSize: 'clamp(21px, 2.4vw, 27px)', color: c.ink,
  },

  chartGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20,
  },
  card: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 16,
    minWidth: 0, minHeight: 260,
  },
  cardHead: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    gap: 10, flexWrap: 'wrap',
  },
  cardTitle: { fontSize: 15, fontWeight: 600, color: c.ink },
  legend: { display: 'flex', gap: 14, fontSize: 12, color: c.muted, flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6 },

  barsWrap: { flex: 1, display: 'flex', alignItems: 'flex-end', gap: 6, minHeight: 150 },
  barCol: {
    flex: 1, height: '100%', display: 'flex', flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  axis: { display: 'flex', fontSize: 11, color: c.muted },

  donutCard: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '22px 26px', display: 'flex', alignItems: 'center', gap: 28,
    minWidth: 0, flexWrap: 'wrap',
  },
  donut: {
    width: 158, height: 158, flex: 'none', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  donutHole: {
    width: 92, height: 92, borderRadius: '50%', background: c.card,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', textAlign: 'center', padding: 4,
  },
  donutValue: { fontFamily: font.serif, fontSize: 21, color: c.ink, lineHeight: 1.1 },
  donutLabel: { fontSize: 11, color: c.muted },
  donutSide: { display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, flex: 1 },
  note: { fontSize: 13, color: c.muted, lineHeight: 1.5 },
};
